<template>
  <div>

    <div class="panel">
      <b-button v-b-modal.help-modal>Help</b-button>
    </div>

    <h3>Unprocessed complaints</h3>

    <loading-indicator
      v-if="isLoading"
    />

    <p v-if="message !== null">
      {{message}}
    </p>

    <template v-if="complaints !== null && complaints.length > 0">
      <rejectable-complaint
        v-for="complaint in complaints"
        :key="complaint.key"
        :complaint="complaint"
        v-on:rejection-selected="rejectComplaint"
      />

      <div
        v-if="mayEditPlayerData"
        class="decision"
      >
        <h3>Decision</h3>
        <p>
          If all complaints are unfounded, reject them.<br>
          Complaints will be deleted.
        </p>
        <b-button @click='rejectComplaints'>
          Reject all complaints
        </b-button>
        <hr>
        <p>
          If all complaints are founded, block chat for player.<br>
          Complaints will be saved.
        </p>

        <form @submit.prevent="blockChat">
          <div class="form-group">
            <label for="reason">Reason</label>
            <textarea
              v-model="reason"
              id="reason"
              class="form-control"
              rows="3"
              maxlength="255"
              required
            />
          </div>

          <div class="form-group">
            <label for="chat-blocking-duration">Chat blocking duration (days)</label>
            <input
              type="number"
              id="chat-blocking-duration"
              v-model="chatBlockingDurationDays"
              class="form-control"
              min="1"
              max="90"
              required
            >
          </div>
          <b-button type="submit" variant="danger">
            Block chat
          </b-button>
        </form>
      </div>

    </template>

    <b-modal id="help-modal" title="Справка" ok-only ok-title="Close">
      <div class="help">
        <p>
          На данной странице показаны жалобы, которые другие игроки отправили на данного игрока.
        </p>
        <div v-if="mayEditPlayerData">
          <p>
            Если жалоба не обоснована, то отклоните ее, нажав на&nbsp;кнопку Rejct (Отклонить).
            Жалоба будет удалена.
          </p>
          <p>
            При нажатии на кнопку Rejct (Отклонить) происходит отклонение одной жалобы.
            Если есть несколько жалоб и все они не обоснованы, то для отклонения сразу всех жалоб нажмите кнопку
            Reject all complaints (Отклонить все жалобы).
          </p>
          <p>
            Если есть обоснованные и необоснованные жалобы,
            то&nbsp;отклоните необоснованные жалобы с помощью кнопки Rejct (Отклонить).
            Должны остаться только обоснованные жалобы.
          </p>
          <p>
            Когда остались только обоснованные жалобы, выберите продолжительность блокировки чата.
            Продолжительность блокировки указывается в днях.
            Затем нажмите кнопку Block chat.
          </p>
        </div>
      </div>
    </b-modal>

  </div>
</template>

<script>
import auth from '../auth';
import { HTTP } from '../http-common';
import notifier from '../notifier';
import LoadingIndicator from './LoadingIndicator.vue';
import RejectableComplaint from './RejectableComplaint.vue';
import { arrayDeleteFirstItemByValue } from '../util';
import { mayEditPlayerData as utilMayEditPlayerData } from '../util';

export default {
  name: 'PlayerInComplaints',

  components: {
    LoadingIndicator,
    RejectableComplaint,
  },

  props: {
    playerId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      actor: auth.getViewer(),
      isLoading: false,
      complaints: null,
      chatBlockingDurationDays: 3,
      reason: '',
    };
  },

  computed: {
    message() {
      if(this.complaints !== null && this.complaints.length === 0) {
        return 'No unprocessed complaints from other players against this player.';
      }
      return null;
    },

    mayEditPlayerData() {
      return utilMayEditPlayerData(this.actor.role);
    },
  },

  created() {
    this.isLoading = true;

    const url = `/player/${this.playerId}/in_complaints`;
    HTTP.get(url)
    .then(response => {
      const data = response.data.data;
      if(data === null) {
        notifier.showError('Invalid server response. data === null.', {sender: this});
        return;
      }

      for(let i = 0; i < data.length; i++) {
        data[i].key = i;
      }
      this.complaints = data;
    })
    .catch(e => {
      notifier.showError(e.response.data);
    })
    .finally(() => {
      this.isLoading = false;
    });
  },

  methods: {
    rejectComplaint(complaint) {
      notifier.deleteSenderNotifications(this);

      const data = new FormData();
      data.append('club_id',     complaint.clubId);
      data.append('reporter_id', complaint.reporterId);
      data.append('msg_id',      complaint.msgId);

      const url = `/player/${this.playerId}/in_complaint/reject`;
      HTTP.post(url, data)
      .then(() => {
        notifier.showSuccess('Complaint rejected.');
        arrayDeleteFirstItemByValue(this.complaints, complaint);
      })
      .catch(e => {
        notifier.showError(e.response.data, {sender: this});
      });
    },

    rejectComplaints() {
      notifier.deleteSenderNotifications(this);

      const complaintIdParts = getComplaintIdParts(this.complaints);

      const url = `/player/${this.playerId}/in_complaints/reject`;
      HTTP.post(
        url,
        {
          complaintIdParts,
        }
      )
      .then(() => {
        notifier.showSuccess('Complaints rejected.');
        this.complaints = [];
      })
      .catch(e => {
        notifier.showError(e.response.data, {sender: this});
      });
    },

    blockChat() {
      notifier.deleteSenderNotifications(this);

      const complaintIdParts = getComplaintIdParts(this.complaints);
      const durationDays = parseInt(this.chatBlockingDurationDays, 10);

      const url = `/player/${this.playerId}/block_chat`;
      HTTP.post(
        url,
        {
          complaintIdParts,
          durationDays,
          reason: this.reason,
        }
      )
      .then(() => {
        notifier.showSuccessAfterTransition('Chat blocked.');
        this.$router.push({name: 'player_chat_block_log', params: {playerId: this.playerId}});
      })
      .catch(e => {
        notifier.showError(e.response.data, {sender: this});
      });
    },
  },
};

function getComplaintIdParts(complaints) {
  return complaints.map(getComplaintIdPart);
}

function getComplaintIdPart(complaint) {
  return {
    clubId:     complaint.clubId,
    reporterId: complaint.reporterId,
    msgId:      complaint.msgId,
  };
}

</script>

<style scoped>
  .panel {
    margin-bottom: 1em;
  }

  .decision p {
    margin-bottom: 1em;
  }
  .decision {
    margin-bottom: 3em;
  }

  #chat-blocking-duration {
    width: 6em;
  }

  .help p {
    margin-bottom: 1em;
  }
</style>
